import { useEffect } from 'react';
import { X } from 'lucide-react';
import { notifySheetOpened, notifySheetClosed } from '../../widgets.jsx';
import { useSheetDrag } from '../../hooks/gesture/use-sheet-drag.js';
import { Z } from '../../lib/z-index.js';

// iOS-Bottom-Sheet (Overnight-Redesign): Backdrop + Sheet mit Swipe-to-dismiss (Griff/Kopf und
// Inhalt bei scrollTop 0), Kopf mit Titel + 44pt-Schließen, eigenständig scrollender Inhalt,
// optionaler fixer Footer im Daumenbereich. Flächen über Tokens (bg-L1 hell / bg-L2 dunkel) —
// funktioniert dadurch auch per Portal außerhalb von .troedel-dark.
//
// Props: open, onClose, title, subtitle, leading, headerRight, children, footer (gepolstert),
//        rawFooter (ohne Wrapper), testId, scrollTestId, footerTestId, backdropTestId, grouped
//        (grouped = iOS-Grouped-Hintergrund: Sheet bg-L0/L2-Ebene, Gruppen heller)
export function SheetShell({
  open, onClose, title, subtitle, leading, headerRight, children, footer, rawFooter,
  testId, scrollTestId, footerTestId, backdropTestId, grouped = false, zIndex,
}) {
  const { sheetRef, backdropRef } = useSheetDrag({ open, onClose });

  useEffect(() => {
    if (!open) return;
    notifySheetOpened();
    return () => notifySheetClosed();
  }, [open]);

  if (!open) return null;

  const surface = grouped
    ? 'bg-[var(--bg-L0)] dark:bg-[var(--bg-L1)]'
    : 'bg-[var(--bg-L1)] dark:bg-[var(--bg-L2)]';

  return (
    <>
      <div
        ref={backdropRef}
        data-testid={backdropTestId}
        className="fixed inset-0 bg-black/40"
        style={{ zIndex: zIndex ?? Z.sheetBackdrop, animation: 'fadeIn 0.2s ease' }}
        onClick={onClose}
      />
      <div
        ref={sheetRef}
        data-testid={testId}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        className={`fixed inset-x-0 bottom-0 flex flex-col rounded-t-[28px] text-[var(--label-primary)] shadow-[var(--shadow-sheet)] ${surface}`}
        style={{
          zIndex: (zIndex ?? Z.sheetBackdrop) + 1,
          maxHeight: 'calc(100dvh - max(12px, env(safe-area-inset-top)))',
          paddingBottom: 'env(safe-area-inset-bottom)',
          animation: 'slideUpSheet 0.35s cubic-bezier(0.32,0.72,0,1) backwards',
          touchAction: 'pan-y',
        }}
      >
        {/* Griff + Kopf: immer ziehbar */}
        <div className="shrink-0" style={{ touchAction: 'none' }}>
          <div className="flex justify-center pb-1 pt-2">
            <div className="bottom-sheet-handle h-[5px] w-9 rounded-full bg-[var(--fill-primary)]" />
          </div>
          {(title || leading || headerRight) && (
            <div className="flex items-center gap-3 pb-2 pl-5 pr-2">
              {leading}
              <div className="min-w-0 flex-1">
                {title && <h2 className="line-clamp-2 text-[20px] font-semibold leading-[1.2] tracking-[-0.01em]">{title}</h2>}
                {subtitle && <div className="mt-0.5 text-[15px] text-[var(--label-secondary)]">{subtitle}</div>}
              </div>
              {headerRight}
              <SheetCloseButton onClick={onClose} />
            </div>
          )}
        </div>

        <div data-testid={scrollTestId} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4">
          {children}
        </div>

        {footer && (
          <div data-testid={footerTestId} className="shrink-0 px-4 pb-4 pt-2">
            {footer}
          </div>
        )}
        {rawFooter}
      </div>
    </>
  );
}

export function SheetCloseButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Schließen"
      className="flex h-11 w-11 shrink-0 items-center justify-center self-start rounded-full active:bg-[var(--fill-quaternary)]"
    >
      <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-[var(--fill-tertiary)]">
        <X className="h-4 w-4 text-[var(--label-secondary)]" strokeWidth={2.6} />
      </span>
    </button>
  );
}
